import type { SetupRepositoryPort } from "@/application/records/ports/SetupRepositoryPort";
import type { AvailableVersions, RecordId } from "@/domain/records/types";
import type { SetupConfig } from "@/domain/records/value-objects/SetupConfig";

/**
 * Setup Repository implementation
 * Uses Records API for setup data access
 */
export class SetupRepository implements SetupRepositoryPort {
  constructor(
    private readonly apiBaseUrl: string,
    private readonly insightApiUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async getByRecordId(
    recordId: RecordId,
    version?: number
  ): Promise<SetupConfig | null> {
    try {
      // Get detailed record which includes setup data
      const url = version
        ? `${this.insightApiUrl}/insight/${recordId}/?version=${version}`
        : `${this.insightApiUrl}/insight/${recordId}/`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No setup data found
        }
        throw new Error(`Failed to fetch setup: ${response.statusText}`);
      }

      const data = await response.json();
      // Map raw API data to SetupConfig
      // This is a simplified mapping - in practice, you'd need to map all fields properly
      return data as SetupConfig;
    } catch (error) {
      console.error("SetupRepository.getByRecordId error:", error);
      throw new Error(
        `Failed to load setup for record ${recordId}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  async save(config: SetupConfig): Promise<void> {
    try {
      // Use dedicated setup update endpoint to update only setup fields
      // without triggering report generation or changing record status
      const url = `${this.insightApiUrl}/insight/${config.recordId}/setup/`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      // Build payload with only setup fields (no generation-related fields)
      // Note: goals and notes are correctly mapped (no swapping needed)
      const payload: Record<string, unknown> = {};

      // Only include fields that are defined in SetupConfig
      if (config.goals !== undefined) {
        payload.goals = config.goals;
      }
      if (config.notes !== undefined) {
        payload.notes = config.notes;
      }
      if (config.guide !== undefined) {
        payload.guide = config.guide;
      }
      if (config.customPrompt !== undefined) {
        payload.custom_prompt = config.customPrompt;
      }
      if (config.participantCount !== undefined) {
        payload.participant_count = config.participantCount;
      }
      if (
        config.respondentNames !== undefined &&
        config.respondentNames.length > 0
      ) {
        payload.respondent_known_names = config.respondentNames.filter((name) =>
          name.trim()
        );
      }
      if (config.language !== undefined) {
        // Language enum value is already a string (e.g., "English", "Russian")
        // Enum values serialize correctly to JSON
        payload.language = config.language as string;
      }
      if (config.sttProvider !== undefined) {
        payload.stt_provider = config.sttProvider;
      }
      // Note: report_language is not in SetupConfig currently,
      // but if needed, it can be added later or passed separately

      const response = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (
            errorData &&
            typeof errorData === "object" &&
            "detail" in errorData
          ) {
            errorMessage = String((errorData as { detail: unknown }).detail);
          }
        } catch {
          // Ignore JSON parsing errors
        }
        throw new Error(errorMessage);
      }

      // 204 No Content or empty response is acceptable
      // Response body is not needed for setup save
    } catch (error) {
      console.error("SetupRepository.save error:", error);
      throw new Error(
        `Failed to save setup: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  async getAvailableVersions(recordId: RecordId): Promise<AvailableVersions> {
    try {
      // Get available versions from meta endpoint
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(
        `${this.insightApiUrl}/insight/${recordId}/meta`,
        {
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch versions: ${response.statusText}`);
      }

      const data = await response.json();
      return data.available_versions || {};
    } catch (error) {
      console.error("SetupRepository.getAvailableVersions error:", error);
      return {};
    }
  }
}
